import { getMobileOS } from './device-util';
import { phone } from 'phone';
import { IAddressDto } from '@pf/shared-common';
import { saveAs } from 'file-saver';

/**
 * Formats a given phone number according to US standards.
 * @param {string} phoneNumber - The phone number to format.
 * @returns {string} - The formatted phone number.
 */
function formatNumber(phoneNumber: string) {
    return phone(phoneNumber, { country: 'US' }).phoneNumber;
}

/**
 * Launches a hyperlink or sets the window location based on the action.
 * @param {string} action - The action to perform (e.g., 'https', 'tel').
 * @param {string} link - The link to launch.
 */
export function launchLink(action: string, link: string) {
    console.log(`launchLink called with action: ${action}, link: ${link}`);

    if (action === 'https') {
        const aRef = document.createElement('a');
        aRef.href = action + ':' + link;
        aRef.setAttribute('target', '_blank');
        aRef.click();
    } else {
        window.location.href = action + ':' + encodeURIComponent(link);
    }
}

/**
 * Formats the phone number and launches a call link.
 * @param {string} phoneNumber - The phone number to call.
 */
export function launchCall(phoneNumber: string) {
    const formattedNumber = formatNumber(phoneNumber);
    if (!formattedNumber) {
        console.warn(
            `Call link not launched due to invalid phone number ${phoneNumber}`
        );
        return;
    }
    launchLink('tel', formattedNumber);
}

/**
 * Formats the phone number and launches a text message link with an optional message.
 * @param {string} phoneNumber - The phone number to text.
 * @param {string} [message] - The message body.
 */
export function launchText(phoneNumber: string, message?: string) {
    let formattedNumber = formatNumber(phoneNumber);
    if (!formattedNumber) {
        console.warn(
            `Text link not launched due to invalid phone number ${phoneNumber}`
        );
        return;
    }
    if (message) {
        formattedNumber += `${
            getMobileOS() === 'iOS' ? '&' : '?'
        }body=${message}`;
    }
    launchLink('sms', formattedNumber);
}

/**
 *    Constructs an email link with optional subject and body parameters and launches it.
 *    @param {string} emailAddress - The email address.
 *    @param {string} [subject] - The email subject.
 *    @param {string} [body] - The email body.
 */
export function launchEmail(
    emailAddress: string,
    subject?: string,
    body?: string
) {
    let link = emailAddress;
    const params = [];
    if (subject) {
        params.push('subject=' + subject);
    }
    if (body) {
        params.push('body=' + body);
    }
    if (params.length > 0) {
        link += `${getMobileOS() === 'iOS' ? '&' : '?'}` + params.join('&');
    }
    launchLink('mailto', link);
}

/**
 *    Constructs a map link using either Apple Maps or Google Maps based on the mobile OS and launches it.
 *    @param {IAddressDto} address - The address to open in the map.
 */
export function launchMap(address: IAddressDto) {
    launchLink(
        'https',
        `//maps.${getMobileOS() === 'iOS' ? 'apple' : 'google'}.com/?q=${
            address.addressLine1
        }${address.addressLine2 || ''}, ${address.city} ${
            address.geoZone ? ', ' + address.geoZone : ''
        } ${address.country ? ', ' + address.country : ''}`
    );
}

export function downloadBase64ImageUrl(base64String: string, fileName: string) {
    // Step 1: Split the Base64 string into MIME type and data
    const [header, base64Data] = base64String.split(',');
    if (!header || !base64Data) {
        throw new Error(
            "Invalid Base64 string format. Expected 'data:[mediatype];base64,...'"
        );
    }

    // Extract MIME type (e.g., 'image/png')
    const mimeType = header.match(/:(.*?);/)?.[1];

    if (!mimeType) {
        throw new Error(
            "Invalid Base64 string format. Expected 'data:[mediatype];base64,...'"
        );
    }

    try {
        // Step 2: Decode Base64 data to binary string
        const binaryString = atob(base64Data);

        // Step 3: Convert binary string to Uint8Array
        const length = binaryString.length;
        const uint8Array = new Uint8Array(length);
        for (let i = 0; i < length; i++) {
            uint8Array[i] = binaryString.charCodeAt(i);
        }

        // Step 4: Create Blob and object URL
        const blob = new Blob([uint8Array], { type: mimeType });

        saveAs(blob, fileName);
    } catch (error) {
        console.error('Failed to save image:', error);
        throw error; // Re-throw to let the caller handle
    }
}
